import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
  showCloseButton?: boolean;
}

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
};

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  showCloseButton = true,
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  // Track where mousedown started to prevent closing when dragging from inside modal
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Track mousedown target to handle click-outside properly
  // This prevents closing when user drags from inside modal to outside (e.g., text selection)
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  // Only close if both mousedown AND mouseup occurred on the overlay
  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      e.target === overlayRef.current &&
      mouseDownTargetRef.current === overlayRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 py-8 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
    >
      <div
        ref={modalRef}
        className={cn(
          "relative w-full bg-white text-gray-900 rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col max-h-[calc(100vh-4rem)] my-auto",
          sizeClasses[size]
        )}
      >
        {/* Close button - absolute positioned when no title */}
        {showCloseButton && !title && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors z-10"
            aria-label="Stäng"
          >
            <X size={20} />
          </button>
        )}

        {/* Header with title - sticky at top */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Stäng"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        {/* Content - scrollable when overflows */}
        <div className={cn("p-6 overflow-y-auto", !title && "pt-12")}>{children}</div>
      </div>
    </div>
  );
}
